// Shared inline email-status cells for transactions.
//
// One source of truth for the four email pills, consumed by BOTH the
// full-page view (`transactionConfig.tsx` "Emaile" section) and the
// queue/pipeline table fields (`transactionPipelineFields.tsx`). Previously
// these were duplicated verbatim across both files and drifted.
//
// Consistent pill rule (every email type — unload confirmation, logistics,
// reminders):
//   • SENT      → green `present` pill; primary click = preview the sent mail;
//                 secondary 🔁 = resend, ONLY when `canResend`.
//   • NOT SENT  → grey `missing` pill; primary click = send; NO secondary
//                 (there is nothing to regenerate/preview before sending).

import React from "react";
import { Tooltip } from "antd";
import {
  EyeOutlined, SendOutlined, ReloadOutlined, LoadingOutlined,
} from "@ant-design/icons";
import { FONT_SIZE } from "@ds/tokens/palette";
import { Inline } from "@ds/layout/Inline";
import { Stack } from "@ds/layout/Stack";
import { DocumentPill } from "@ds/tables/cells/DocumentPill";
import { EmptyValue } from "@ds/display/EmptyValue";
import type { EmailSend } from "@shared/types/employee";
import type { TransactionActionsContextValue } from "./hooks/TransactionActionsContext";
import { useFormat } from "@ds/display/useNumberFormat";
import { useTranslation } from "react-i18next";

type TxExtras = TransactionActionsContextValue | null | undefined;

const iconStyle = { fontSize: FONT_SIZE.label } as const;

// ─── Shared pill primitive ───────────────────────────────────────────────────
// Encapsulates the sent/not-sent rule so every email type renders identically.

function EmailStatePill({
  sent, label, canResend = true, loading, onPreview, onSend, testId, resendTestId,
}: {
  sent: boolean;
  label: string;
  canResend?: boolean;
  loading?: boolean;
  onPreview?: () => void;
  onSend?: () => void;
  testId?: string;
  resendTestId?: string;
}): React.ReactElement {
  if (sent) {
    return (
      <DocumentPill
        state="present"
        reviewTone="approved"
        label={label}
        icon={<EyeOutlined style={iconStyle} />}
        onOpen={onPreview}
        testId={testId}
        secondaryAction={canResend && onSend ? {
          kind: "resend",
          icon: loading ? <LoadingOutlined /> : <ReloadOutlined style={iconStyle} />,
          onClick: onSend,
          title: "Wyślij ponownie",
          testId: resendTestId,
        } : undefined}
      />
    );
  }
  return (
    <DocumentPill
      state="missing"
      reviewTone="none"
      label={label}
      icon={loading ? <LoadingOutlined /> : <SendOutlined style={iconStyle} />}
      onOpen={onSend}
      testId={testId}
    />
  );
}

// ─── Unload confirmation (Potwierdzenie rozładunku) ──────────────────────────

export function UnloadConfirmationInlineCell({
  emails, isHidden, transactionId, extras,
}: {
  emails: EmailSend[];
  isHidden: boolean;
  transactionId: number;
  extras: TxExtras;
}): React.ReactElement | null {
  if (isHidden) return null;
  const sent = emails.find((e) => e.sent_at);
  const loading = (extras?.external.preparingEmailTxId ?? null) === transactionId;
  const onPreview = extras && sent ? () => extras.external.onPreviewEmail(sent.id) : undefined;
  const onSend = extras
    ? () => extras.external.onSendEmail(transactionId, "unloading_confirmation")
    : undefined;
  return (
    <EmailStatePill
      sent={!!sent}
      label={sent ? "Wysłana" : "Wyślij"}
      loading={loading}
      onPreview={onPreview}
      onSend={onSend}
      testId={`cell-email-unloading_confirmation-${transactionId}`}
      resendTestId={`cell-email-unloading_confirmation-resend-${transactionId}`}
    />
  );
}

// ─── Logistics e-mails (per type) ────────────────────────────────────────────

export function LogisticsEmailsInlineCell({
  emails, defs, transactionId, extras, layout = "inline",
}: {
  emails: EmailSend[];
  defs: ReadonlyArray<{ type: string; label: string; checkStatus: string | null; canResend: boolean }>;
  transactionId: number;
  extras: TxExtras;
  /** Pill arrangement. `"inline"` (default) = wrapping row; `"stack"` = one pill per line. */
  layout?: "inline" | "stack";
}): React.ReactElement {
  const loading = extras?.external.preparingEmailTxId === transactionId;
  const pills = (
    <>
      {defs.map(({ type, label, checkStatus, canResend }) => {
        if (checkStatus === "not_required") return null;
        const sent = emails.find((e) => e.email_type === type && e.sent_at);
        const onPreview = extras && sent ? () => extras.external.onPreviewEmail(sent.id) : undefined;
        const onSend = extras ? () => extras.external.onSendEmail(transactionId, type) : undefined;
        return (
          <EmailStatePill
            key={type}
            sent={!!sent}
            label={label}
            canResend={canResend}
            loading={loading}
            onPreview={onPreview}
            onSend={onSend}
            testId={`cell-email-${type}-${transactionId}`}
            resendTestId={`cell-email-${type}-resend-${transactionId}`}
          />
        );
      })}
    </>
  );
  return layout === "stack"
    ? <Stack gap="xs" align="start">{pills}</Stack>
    : <Inline gap="xs" align="center" wrap>{pills}</Inline>;
}

// ─── Reminders ───────────────────────────────────────────────────────────────
// Both reminder rows now render as pills (was a bare Tag + a big ActionButton).

/** "Ostatnie przypomnienie" — the most recently sent reminder. Preview + resend. */
export function LastReminderInlineCell({
  reminders, transactionId, extras,
}: {
  /** Both call sites pre-filter to sent reminders, so `sent_at` is guaranteed. */
  reminders: (EmailSend & { sent_at: string })[];
  transactionId: number;
  extras: TxExtras;
}): React.ReactElement {
  const { t } = useTranslation();
  const fmt = useFormat();
  if (!reminders.length) return <EmptyValue />;
  const last = reminders[0];
  const date = new Date(last.sent_at);
  const label = fmt.isToday(date) ? "Dziś" : fmt.date(date);
  const loading = (extras?.external.preparingEmailTxId ?? null) === transactionId;
  const onPreview = extras ? () => extras.external.onPreviewEmail(last.id) : undefined;
  const onSend = extras
    ? () => extras.external.onSendEmail(transactionId, "documents_reminder")
    : undefined;
  return (
    <Tooltip title={t("transactions.pipeline.reminder_sent_tooltip", { dateTime: fmt.dateTime(date) })}>
      <span>
        <EmailStatePill
          sent
          label={label}
          loading={loading}
          onPreview={onPreview}
          onSend={onSend}
          testId={`cell-email-documents_reminder-${transactionId}`}
          resendTestId={`cell-email-documents_reminder-resend-${transactionId}`}
        />
      </span>
    </Tooltip>
  );
}

/** "Wyślij przypomnienie" — the send action, as a pill. */
export function SendReminderInlineCell({
  reminders, transactionId, extras,
}: {
  reminders: EmailSend[];
  transactionId: number;
  extras: TxExtras;
}): React.ReactElement | null {
  const fmt = useFormat();
  const sentToday = reminders.some((e) => fmt.isToday(e.sent_at ?? null));
  if (sentToday) {
    return <DocumentPill state="present" reviewTone="approved" label="Wysłano dziś" />;
  }
  if (!extras) return null;
  const loading = extras.external.preparingEmailTxId === transactionId;
  return (
    <EmailStatePill
      sent={false}
      label="Wyślij"
      loading={loading}
      onSend={() => extras.external.onSendEmail(transactionId, "documents_reminder")}
      testId={`cell-email-documents_reminder-${transactionId}`}
    />
  );
}
